import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import AiChatBackground from "@src/features/assistantChat/AiChatBackground";
import { Button } from "@src/lib/ui/Button";
import { OpusAgentComposer } from "@src/features/opus-agent/OpusAgentComposer";
import { useOpusAgentAttachments } from "@src/features/opus-agent/attachments/useOpusAgentAttachments";
import { Dialog, DialogContent } from "@src/lib/ui/Dialog";
import XIcon from "@src/lib/icons/12px/x";
import Text from "@src/lib/ui/Text";
import {
  AiChatType,
  Ff,
  LibraryItemType,
  OpusAgentPlaybook,
} from "@src/types.generated";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { useChatSidebar } from "@src/features/aiAssist/hooks/useChatSidebar";
import { useAgentPanelGates } from "@src/features/opus-agent/agentPanel";
import { useCreateCourseMutation } from "@src/features/marketplace/category-nav/CategorySection.generated";
import { useCreateModuleMutation } from "@src/features/libraryItem/module/hooks/useNewModule.generated";
import { useCheckInsPageHeaderCreateSkillMutation } from "@src/features/library/check-ins/CheckInsPageHeader.generated";
import { CONTENT_LIBRARY_REFETCH_QUERIES } from "@src/lib/contexts/TagContext/TagContext";
import { emptyTranslationSet } from "@src/features/knowledgeCheck/utils/prompts";
import { useToast } from "@src/lib/hooks/useToast";
import {
  queueFirstMessageAttachments,
  queueFirstMessagePlaybooks,
} from "@src/features/opus-agent/firstMessagePlaybooks";
import NewModuleFromArticulateUploadDialog from "./NewModuleFromArticulateUploadDialog";

export type AgentBuilderItemKind =
  | "course"
  | "module"
  | "checklist"
  | "checkIn"
  | "audit";

type KindConfig = {
  playbook: OpusAgentPlaybook;
  dialogTitle: string;
  contentTitle: string;
  contentDescription: string;
  placeholder: string;
  newFromScratchLabel: string;
  callsite: string;
};

export const AGENT_BUILDER_KINDS: Record<AgentBuilderItemKind, KindConfig> = {
  course: {
    playbook: OpusAgentPlaybook.CourseAuthoring,
    dialogTitle: "New Course",
    contentTitle: "Create course with AI",
    contentDescription:
      "Describe your training goals, topics and roles.\nOpus will create the course and build it with you in the builder.",
    placeholder:
      "Example: Barista latte art basics - cover milk steaming, pouring techniques, and hygiene.",
    newFromScratchLabel: "New course from scratch",
    callsite: "agent_builder_create_course",
  },
  module: {
    playbook: OpusAgentPlaybook.ModuleAuthoring,
    dialogTitle: "New Module",
    contentTitle: "Create module with AI",
    contentDescription:
      "Describe your training goals, topics and roles.\nOpus will create the module and build it with you in the builder.",
    placeholder:
      "Example: Onboarding for new servers - cover menu knowledge, service steps, and safety protocols.",
    newFromScratchLabel: "New module from scratch",
    callsite: "agent_builder_create_module",
  },
  checklist: {
    playbook: OpusAgentPlaybook.ChecklistAuthoring,
    dialogTitle: "New Checklist",
    contentTitle: "Create checklist with AI",
    contentDescription:
      "Describe your operational tasks, procedures, or checklists.\nOpus will create the checklist and build it with you in the builder.",
    placeholder:
      "Example: Daily refrigerator temperature checks - record temps for walk-in fridge, freezer, and prep stations.",
    newFromScratchLabel: "New checklist from scratch",
    callsite: "agent_builder_create_task_list",
  },
  checkIn: {
    playbook: OpusAgentPlaybook.CheckInAuthoring,
    dialogTitle: "New Check-in",
    contentTitle: "Create check-in with AI",
    contentDescription:
      "Describe your check-in requirements, questions, and evaluation criteria.\nOpus will create the check-in and build it with you in the builder.",
    placeholder:
      "Example: Barista latte art check-in - evaluate milk steaming technique, pouring consistency, and presentation quality.",
    newFromScratchLabel: "New check-in from scratch",
    callsite: "agent_builder_create_skill",
  },
  audit: {
    playbook: OpusAgentPlaybook.AuditAuthoring,
    dialogTitle: "New Audit",
    contentTitle: "Create audit with AI",
    contentDescription:
      "Describe your audit requirements, evaluation criteria, and scoring system.\nOpus will create the audit and build it with you in the builder.",
    placeholder:
      "Example: Food safety audit - evaluate temperature logs, cleanliness standards, and compliance with health regulations.",
    newFromScratchLabel: "New audit from scratch",
    callsite: "agent_builder_create_audit",
  },
};

const SKILL_TYPES: Record<
  Exclude<AgentBuilderItemKind, "course" | "module">,
  LibraryItemType
> = {
  checklist: LibraryItemType.Task,
  checkIn: LibraryItemType.Skill,
  audit: LibraryItemType.Audit,
};

type CreateResult = { libraryItemId: string | null; errorMessage?: string };

export const useAgentBuilderCreationEnabled = (): boolean => {
  const { askOpus } = useAgentPanelGates();
  const writesEnabled = useFeatureFlag(Ff.OpusAgentWrites);
  const killSwitchEnabled = useFeatureFlag(Ff.OpusAgentKillSwitch);
  return askOpus && writesEnabled && !killSwitchEnabled;
};

const useCreateLibraryItem = (
  kind: AgentBuilderItemKind,
): (() => Promise<CreateResult>) => {
  const [createCourse] = useCreateCourseMutation({ variables: { name: "" } });
  const [createModule] = useCreateModuleMutation({
    variables: { input: { name: "" } },
    refetchQueries: CONTENT_LIBRARY_REFETCH_QUERIES,
  });
  const [createSkill] = useCheckInsPageHeaderCreateSkillMutation();
  return async () => {
    if (kind === "course") {
      const res = await createCourse();
      return {
        libraryItemId: res.data?.createCourse?.course?.libraryItem.id ?? null,
        errorMessage: res.data?.createCourse?.error?.message ?? undefined,
      };
    }
    if (kind === "module") {
      const res = await createModule();
      return {
        libraryItemId: res.data?.createModule?.module?.libraryItem.id ?? null,
      };
    }
    const res = await createSkill({
      variables: {
        input: {
          type: SKILL_TYPES[kind],
          name: emptyTranslationSet(),
          description: emptyTranslationSet(),
        },
      },
    });
    return {
      libraryItemId: res.data?.createSkill?.skill?.libraryItem.id ?? null,
    };
  };
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: AgentBuilderItemKind;
  initialInput?: string;
  onItemCreated?: (libraryItemId: string) => void;
}

const AgentBuilderCreationDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  kind,
  initialInput,
  onItemCreated,
}) => {
  const config = AGENT_BUILDER_KINDS[kind];
  const router = useRouter();
  const { addErrorToast } = useToast();
  const [input, setInput] = useState(initialInput ?? "");
  const [creating, setCreating] = useState(false);
  const creatingRef = useRef(false);
  const [isArticulateDialogOpen, setIsArticulateDialogOpen] = useState(false);
  const createLibraryItem = useCreateLibraryItem(kind);
  // Files and Opus items for the first message; they ride to the builder's
  // Ask Opus panel with the prompt.
  const attachments = useOpusAgentAttachments();
  // The dialogs stay mounted while closed: a reopened dialog starts with no
  // pills, and nothing keeps polling for a dialog nobody sees.
  const { clear: clearAttachments } = attachments;
  useEffect(() => {
    if (!open) clearAttachments();
  }, [open, clearAttachments]);
  const { startNewConversation, setInitialQuery } = useChatSidebar(
    AiChatType.KnowledgeBase,
  );

  const setBusy = (busy: boolean) => {
    creatingRef.current = busy;
    setCreating(busy);
  };

  const start = async (prompt: string | null) => {
    if (creatingRef.current) return;
    // Taken before the dialog closes: closing clears the pills.
    const ready = prompt ? attachments.sendable() : [];
    setBusy(true);
    const { libraryItemId, errorMessage } = await createLibraryItem().catch(
      (): CreateResult => ({ libraryItemId: null }),
    );
    if (!libraryItemId) {
      setBusy(false);
      addErrorToast({ message: errorMessage, callsite: config.callsite });
      return;
    }
    onItemCreated?.(libraryItemId);
    setInput("");
    onOpenChange(false);
    await router
      .push({
        pathname: "/library/library-item/[id]",
        query: { id: libraryItemId },
      })
      .catch(() => false);
    setBusy(false);
    const landed =
      window.location.pathname === `/library/library-item/${libraryItemId}`;
    if (!landed || !prompt) return;
    queueFirstMessagePlaybooks([config.playbook]);
    queueFirstMessageAttachments(ready);
    startNewConversation();
    setInitialQuery(prompt);
  };

  // Also reached from dictation's stop-and-send, which skips the composer's
  // own checks.
  const handleSubmit = () => {
    const prompt = input.trim();
    if (prompt && !attachments.gate.blocked) void start(prompt);
  };

  return (
    <>
      <Dialog
        open={open && !isArticulateDialogOpen}
        onOpenChange={(nextOpen) => {
          if (nextOpen || !creatingRef.current) onOpenChange(nextOpen);
        }}
      >
        <DialogContent
          className="min-w-[600px] gap-0 overflow-hidden p-0"
          hideCloseButton
        >
          <div className="flex h-[44px] items-center justify-between border-b p-3 pr-2">
            <Text variant="compact-title">{config.dialogTitle}</Text>
            <Button
              variant="secondary"
              size="icon-xs"
              className="text-muted-foreground shadow-none"
              disabled={creating}
              onClick={() => onOpenChange(false)}
              aria-label="Close"
            >
              <XIcon />
            </Button>
          </div>
          <div className="relative flex flex-col gap-2.5 overflow-hidden p-8">
            <AiChatBackground />
            <Text
              variant="body-title"
              className="text-center"
              data-testid="agent-builder-creation-title"
            >
              {config.contentTitle}
            </Text>
            <Text
              variant="compact-description"
              className="mb-4 px-4 text-center text-muted-foreground"
              multiline
            >
              {config.contentDescription}
            </Text>
            <OpusAgentComposer
              input={input}
              onInputChange={setInput}
              onSubmit={handleSubmit}
              attachments={attachments}
              placeholder={config.placeholder}
              inputClassName="min-h-[140px]"
              disabled={creating}
              submitLabel="Start building"
              autoFocus
            />
            <Text
              variant="compact-description"
              className="mt-2 text-center text-muted-foreground"
            >
              Need help?{" "}
              <a
                href="https://help.opus.so/en/articles/12002784-how-to-build-training-using-ai-assist"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline decoration-dotted"
              >
                Read the guide
              </a>
            </Text>
          </div>
          <div className="flex flex-col items-center justify-center border-t border-border bg-muted p-3">
            <Text
              variant="compact-description"
              className="text-center text-muted-foreground"
            >
              or
            </Text>
            <div className="mt-3 flex items-center gap-5 self-stretch">
              <Button
                variant="outline"
                className="flex-1 text-primary"
                disabled={creating}
                onClick={() => void start(null)}
              >
                {config.newFromScratchLabel}
              </Button>
              {kind === "course" && (
                <Button
                  variant="outline"
                  className="flex-1 text-primary"
                  disabled={creating}
                  onClick={() => {
                    onOpenChange(false);
                    router.push("/marketplace");
                  }}
                >
                  Browse marketplace courses
                </Button>
              )}
              {kind === "module" && (
                <Button
                  variant="outline"
                  className="flex-1 text-primary"
                  disabled={creating}
                  onClick={() => setIsArticulateDialogOpen(true)}
                >
                  Import Articulate Rise 360
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {kind === "module" && (
        <NewModuleFromArticulateUploadDialog
          open={isArticulateDialogOpen}
          onOpenChange={(nextOpen) => {
            setIsArticulateDialogOpen(nextOpen);
            if (!nextOpen) onOpenChange(false);
          }}
        />
      )}
    </>
  );
};

export default AgentBuilderCreationDialog;
